import { z } from "zod";

/**
 * The names a setup step may reference to say when it is finished.
 *
 * The implementations live in bedrock (`services/setup-checks.ts`); only the
 * names live here, because two things that cannot import each other both need
 * them: the plan-file generator, which rejects an unknown name at build time,
 * and the client, which reads the results back.
 */
export const SETUP_CHECK_NAMES = [
  "has-brand-context",
  "has-customized-brand",
  "can-take-orders",
  "has-user-integration",
  "has-custom-domain",
  "has-published",
  "has-qualified-traffic",
  "has-connected-assistant",
  "has-personalized-assistant",
  "has-insights-report",
  "has-project-skill",
  "has-scheduled-task",
  "has-growth-workflow",
] as const;

export const GROWTH_WORKFLOW_CHECK_NAMES = [
  "has-insights-report",
  "has-project-skill",
  "has-scheduled-task",
] as const satisfies readonly (typeof SETUP_CHECK_NAMES)[number][];

export const ACTIVATION_PLAN_CHECK_NAMES = [
  "has-customized-brand",
  "has-personalized-assistant",
  "has-user-integration",
  "has-published",
  "has-custom-domain",
  "has-qualified-traffic",
  ...GROWTH_WORKFLOW_CHECK_NAMES,
] as const satisfies readonly (typeof SETUP_CHECK_NAMES)[number][];

export const setupCheckNameSchema = z
  .enum(SETUP_CHECK_NAMES)
  .describe("SetupCheckName");

export type SetupCheckName = (typeof SETUP_CHECK_NAMES)[number];

export const setupCheckResultSchema = z
  .object({
    complete: z.boolean(),
    current: z.number().int().nonnegative().optional(),
    target: z.number().int().positive().optional(),
  })
  .refine(
    ({ current, target }) => {
      return (
        (current === undefined && target === undefined) ||
        (current !== undefined && target !== undefined)
      );
    },
    {
      message: "Setup check progress requires both current and target",
    },
  )
  .describe("SetupCheckResult");

export type SetupCheckResult = z.infer<typeof setupCheckResultSchema>;

export function isSetupCheckName(value: string): value is SetupCheckName {
  return setupCheckNameSchema.safeParse(value).success;
}
